<template>
  <div class="filter-editor">
    <hr-filter-chip-group
      v-if="operators.length > 1"
      :model-value="modelValue.filterCriteriaType"
      :options="operators"
      @update:model-value="onOperatorChange"
    />
    <!-- The pane headline already names the criterion, so the field does not repeat it. -->
    <hr-text-field
      :model-value="text"
      :autofocus="autofocus"
      @update:model-value="onTextChange"
    />
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';

import { HrFilterChipGroup, HrTextField } from 'src/design-system';

import shouldAutofocus from '../autofocus';
import { getAvailableTextCriteriaTypes } from '../criteriaTypes';

/**
 * Free-text filter: how to compare, then what to compare against.
 */
defineOptions({ name: 'TextEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const autofocus = shouldAutofocus();

const operators = computed(() => getAvailableTextCriteriaTypes(props.criterion));

// The stored value is whatever the statement holds, which starts as null.
const text = computed(() => (typeof props.modelValue.filteredItems === 'string'
  ? props.modelValue.filteredItems
  : ''));

function patch(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

/*
 * The excluding operator is not just a comparison: it also flips the statement's negative flag,
 * which is what puts "exclude" in front of the chip's label. Setting one without the other leaves
 * a filter that excludes but does not say so.
 */
function onOperatorChange(value) {
  const operator = operators.value.find((option) => option.value === value);

  patch({
    filterCriteriaType: value,
    filterNegative: operator?.isNegative || false,
  });
}

function onTextChange(value) {
  patch({ filteredItems: value });
}

/*
 * A criterion allowing one comparison hides the row, so nothing ever emits that comparison and the
 * statement keeps the null the engine reads as "contains". A criterion restricted to isEqualTo
 * would then filter as a substring match, which is the opposite of what it declared.
 */
watch(operators, (available) => {
  const [only] = available;

  if (available.length === 1 && props.modelValue.filterCriteriaType !== only.value) {
    onOperatorChange(only.value);
  }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.filter-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
